/**
 * Holdfast and Mutual grammars: Long Hold (both), Listening Sails / Holdfast, Commons / Mutual.
 * Holdfast: sealed spun drums, spheres ≥ 30 m, straight-edged sealed sails (no petals, no facets).
 * Mutual: First Rush relics wrapped in a mismatched bolt-on village with guyed masts (the only lattice-free clutter).
 */
import type { Rng } from '../../core/rng.ts'
import { detailRng, gateQuadrant, lumClass, lumScale, part, radial, regionIndex, rf, ri, type PartOpts } from './kit.ts'
import type { FamilyGrammar, MegaPart, PrimKind, Vec3 } from './types.ts'

const TAU = Math.PI * 2

/** One mismatched Mutual bolt-on pod, centred at `p`, pushed outward along `out` by its own half size. */
function pod(rng: Rng, p: Vec3, out: readonly [number, number, number], scale: number, step: number): MegaPart {
  const kinds: readonly PrimKind[] = ['drum', 'sphere', 'podTier', 'slab', 'drum', 'frustum']
  const prim = kinds[ri(rng, kinds.length)]
  const w = rf(rng, 70, 190) * scale
  const h = rf(rng, 60, 240) * scale
  const push = w * 0.45
  const pos: Vec3 = [p[0] + out[0] * push, p[1] + out[1] * push, p[2] + out[2] * push]
  const o: PartOpts = { step, role: 'accent', rot: [0, rf(rng, 0, TAU), rf(rng, -0.25, 0.25)], taper: rf(rng, 0.6, 1) }
  return part(prim, pos, [w, h, w * rf(rng, 0.7, 1.1)], o)
}

/** A guyed antenna mast from `base` along unit `d` (a thin sealed spar with a tip blister). */
function mast(base: Vec3, d: readonly [number, number, number], len: number, step: number): MegaPart[] {
  const mid: Vec3 = [base[0] + d[0] * len * 0.5, base[1] + d[1] * len * 0.5, base[2] + d[2] * len * 0.5]
  const tilt = Math.atan2(-d[0], Math.hypot(d[1], d[2]))
  const lean = Math.atan2(d[2], d[1])
  const tip: Vec3 = [base[0] + d[0] * len, base[1] + d[1] * len, base[2] + d[2] * len]
  return [
    part('drum', mid, [16, len, 16], { rot: [lean, 0, tilt], step, role: 'accent' }),
    part('sphere', tip, [44, 44, 44], { step, role: 'accent' }),
  ]
}

// ── Long Hold / Holdfast ───────────────────────────────────────────────────────────────────────────────────────────
export const longHoldHoldfast: FamilyGrammar = {
  kind: 'longHold',
  maker: 'holdfast',
  slots: [
    { name: 'drums', options: 4 }, // 2/3/4/5 spun drums
    { name: 'step', options: 4 }, // taper up, inverted, waist, bulge
    { name: 'sails', options: 4 }, // pair, tri, cross, one-sided pair
    { name: 'sailSize', options: 4, site: lumClass }, // dim star → monument sails
    { name: 'cap', options: 4 }, // dome, stepped cap, spindle, sphere on a neck
    { name: 'foot', options: 4 }, // frustum foot, sphere foot, twin lobes, skirt
    { name: 'break', options: 4 }, // none, offset stack, high sails, satellite stack
    { name: 'finish', options: 4, site: regionIndex }, // cable-wrap bands: gate sleek → rim banded
  ],
  build(g, site) {
    const [nI, stepI, sailI, , capI, footI, breakI, finI] = g.slots
    const p = g.props
    const H = 2500 + 900 * p[0]
    const out: MegaPart[] = []
    // Foot (step 1).
    let footTop: number
    const footW = 0.5 * H * (0.9 + 0.2 * p[1])
    if (footI === 0) {
      footTop = 0.15 * H
      out.push(part('frustum', [0, footTop / 2, 0], [footW, footTop, footW], { taper: 0.62 }))
    } else if (footI === 1) {
      footTop = 0.34 * H
      out.push(part('sphere', [0, footTop / 2, 0], [0.42 * H, footTop, 0.42 * H]))
    } else if (footI === 2) {
      footTop = 0.24 * H
      out.push(part('sphere', [-0.13 * H, footTop / 2, 0], [0.28 * H, footTop, 0.28 * H]))
      out.push(part('sphere', [0.13 * H, footTop / 2, 0], [0.28 * H, footTop, 0.28 * H]))
    } else {
      footTop = 0.12 * H
      out.push(part('drum', [0, 0.04 * H, 0], [footW * 1.05, 0.08 * H, footW * 1.05]))
      out.push(part('frustum', [0, 0.1 * H, 0], [footW * 0.8, 0.04 * H, footW * 0.8], { taper: 0.6 }))
    }
    // Drums (steps 2 and 3): stepped diameters, a neck gap between each.
    const n = [2, 3, 4, 5][nI]
    const D0 = 0.36 * H * (0.9 + 0.2 * p[2])
    const stackH = 0.62 * H * (0.9 + 0.2 * p[3])
    const gap = 0.025 * H
    const dh = (stackH - gap * (n - 1)) / n
    let y = footTop + gap
    let topD = D0
    for (let i = 0; i < n; i++) {
      const t = (i + 0.5) / n
      let f: number
      if (stepI === 0) f = 1 - 0.5 * t
      else if (stepI === 1) f = 0.55 + 0.5 * t
      else if (stepI === 2) f = (1 - 0.45 * Math.sin(Math.PI * t)) * (i % 2 === 1 ? 0.85 : 1)
      else f = (0.6 + 0.45 * Math.sin(Math.PI * t)) * (i % 2 === 1 ? 1 : 0.88)
      const D = D0 * f
      const h = dh * (i % 2 === 0 ? 1.08 : 0.92)
      const xOff = breakI === 1 ? (i % 2 === 0 ? -1 : 1) * 0.05 * H : 0
      const step = i < Math.ceil(n / 2) ? 2 : 3
      if (i > 0) out.push(part('drum', [xOff * 0.5, y - gap / 2, 0], [D * 0.42, gap * 1.1, D * 0.42], { step }))
      out.push(part('drum', [xOff, y + h / 2, 0], [D, h, D], { step }))
      for (let b = 0; b < finI; b++) {
        const by = y + h * ((b + 1) / (finI + 1))
        out.push(part('drum', [xOff, by, 0], [D * 1.07, 0.014 * H, D * 1.07], { step: 5, role: 'accent' }))
      }
      y += h + gap
      topD = D
    }
    const top = y - gap
    // Cap (step 5).
    if (capI === 0) out.push(part('sphere', [0, top, 0], [topD * 0.92, topD * 0.55, topD * 0.92], { step: 5 }))
    else if (capI === 1) {
      out.push(part('frustum', [0, top + 0.03 * H, 0], [topD * 0.8, 0.06 * H, topD * 0.8], { taper: 0.6, step: 5 }))
      out.push(part('frustum', [0, top + 0.09 * H, 0], [topD * 0.42, 0.06 * H, topD * 0.42], { taper: 0.5, step: 5 }))
    } else if (capI === 2) {
      out.push(part('frustum', [0, top + 0.16 * H, 0], [topD * 0.3, 0.32 * H, topD * 0.3], { taper: 0.08, step: 5 }))
    } else {
      out.push(part('drum', [0, top + 0.04 * H, 0], [topD * 0.18, 0.08 * H, topD * 0.18], { step: 5 }))
      out.push(part('sphere', [0, top + 0.08 * H + topD * 0.2, 0], [topD * 0.45, topD * 0.45, topD * 0.45], { step: 5 }))
    }
    if (breakI === 3) {
      const sx = 0.32 * H
      out.push(part('drum', [sx, footTop + 0.16 * H, 0.05 * H], [0.16 * H, 0.32 * H, 0.16 * H], { step: 3 }))
      out.push(part('sphere', [sx, footTop + 0.32 * H, 0.05 * H], [0.16 * H, 0.1 * H, 0.16 * H], { step: 3 }))
    }
    // Sails off the still foot (step 4), sized against the star but capped against the drums (M1, round 2): at the
    // dimmest star the sails' summed side area stays under ~0.9 × the drum stack's; brighter stars scale down from there.
    const ls = lumScale(site)
    const nSails = sailI === 1 ? 3 : sailI === 2 ? 4 : 2
    const cap = Math.sqrt((0.9 * D0 * 0.8 * stackH) / (nSails * 0.85))
    const L = Math.min(0.3 * H, cap) * (ls / 1.7)
    const Hs = Math.min(L, 0.8 * stackH)
    const sailY = breakI === 2 ? footTop + 0.3 * H : footTop + Hs * 0.35
    const bearings: readonly number[] =
      sailI === 0 ? [0, Math.PI] : sailI === 1 ? [0, TAU / 3, (2 * TAU) / 3] : sailI === 2 ? [0, TAU / 4, TAU / 2, (3 * TAU) / 4] : [-0.6, 0.6]
    const r0 = D0 * 0.42 + L / 2
    for (const b of bearings) {
      out.push(radial('plate', b + p[4] * 0.5, r0, sailY, [L, Hs, 22], { step: 4, role: 'signature', taper: 0.7 }))
    }
    return out
  },
}

// ── Long Hold / Mutual ─────────────────────────────────────────────────────────────────────────────────────────────
export const longHoldMutual: FamilyGrammar = {
  kind: 'longHold',
  maker: 'mutual',
  slots: [
    { name: 'relicPose', options: 4 }, // upright, 20° lean, lying, 40° lean
    { name: 'spiral', options: 4 }, // 1, 1.5, 2, 3 turns
    { name: 'density', options: 4 }, // 24/36/48/64 pods
    { name: 'masts', options: 4 }, // 1..4 guyed masts
    { name: 'brokenEnd', options: 4 }, // top broken, bottom broken, both capped, sheared
    { name: 'perch', options: 3 }, // free, small rock, perched on a big rock
    { name: 'collectors', options: 4, site: lumClass }, // collector plates sized to the star
    { name: 'outriggers', options: 4, site: regionIndex }, // gate tidy → rim sprawling arms
  ],
  build(g, site) {
    const [poseI, spiralI, densI, mastI, endI, perchI, , outI] = g.slots
    const p = g.props
    const H = 2600 + 800 * p[0]
    const out: MegaPart[] = []
    const D = 0.4 * H * (0.9 + 0.2 * p[1])
    const L = 0.85 * H * (0.9 + 0.15 * p[2])
    const th = [0, 0.35, Math.PI / 2, 0.7][poseI]
    const a: Vec3 = [-Math.sin(th), Math.cos(th), 0]
    const u: Vec3 = [Math.cos(th), Math.sin(th), 0]
    const base = poseI === 2 ? D / 2 + 0.05 * H : 0.05 * H
    const ctr: Vec3 = [-a[0] * 0 + (poseI === 2 ? 0 : a[0] * L * 0.5), base + (poseI === 2 ? 0 : a[1] * L * 0.5), 0]
    const at = (t: number, r: number, phi: number): Vec3 => [
      ctr[0] + a[0] * (t - 0.5) * L + r * (u[0] * Math.cos(phi)),
      ctr[1] + a[1] * (t - 0.5) * L + r * (u[1] * Math.cos(phi)),
      r * Math.sin(phi),
    ]
    out.push(part('drum', ctr, [D, L, D], { rot: [0, 0, th], role: 'relic' }))
    // Rock perch (step 1).
    if (perchI > 0) {
      const rs = perchI === 1 ? 0.3 * H : 0.55 * H
      out.push(part('rock', [0.05 * H, rs * 0.25, 0], [rs * 1.3, rs * 0.7, rs], { step: 1, role: 'work', seed: g.detail }))
    }
    // Ends (step 2): a capped end is a dome; a broken end is a ring of jagged teeth.
    const ends: readonly [number, boolean][] = [
      [1, endI === 1 || endI === 2],
      [0, endI === 0 || endI === 2],
    ]
    const tr = detailRng(g.detail, 11)
    for (const [t, capped] of ends) {
      const e = at(t, 0, 0)
      const sgn = t === 1 ? 1 : -1
      if (capped) out.push(part('sphere', e, [D * 0.96, D * 0.4, D * 0.96], { rot: [0, 0, th], step: 2, role: 'relic' }))
      else {
        for (let k = 0; k < 9; k++) {
          const phi = (k / 9) * TAU
          const h = rf(tr, 0.04, 0.16) * H
          const q = at(t + (sgn * h) / (2 * L), D / 2 - 20, phi)
          out.push(part('slab', q, [60, h, 70], { rot: [0, 0, th], step: 2, role: 'relic' }))
        }
      }
    }
    if (endI === 3) {
      const e = at(1, 0, 0)
      out.push(part('slab', e, [D * 1.02, 0.05 * H, D * 1.02], { rot: [0.5, 0, th + 0.4], step: 2, role: 'relic' }))
    }
    // The climbing spiral village (steps 2..4).
    const turns = [1, 1.5, 2, 3][spiralI]
    const nPods = [24, 36, 48, 64][densI]
    const pr = detailRng(g.detail, 12)
    for (let i = 0; i < nPods; i++) {
      const t = 0.06 + (0.88 * i) / nPods
      const phi = turns * TAU * t + rf(pr, -0.3, 0.3)
      const q = at(t, D / 2, phi)
      const o: [number, number, number] = [u[0] * Math.cos(phi), u[1] * Math.cos(phi), Math.sin(phi)]
      out.push(pod(pr, q, o, 1 + 0.4 * p[3], 2 + Math.floor((3 * i) / nPods)))
    }
    // Collector plates (step 4) sized to the star.
    const cs = lumScale(site) * 0.26 * H
    for (let k = 0; k < 2; k++) {
      const q = at(0.25 + 0.5 * k, D / 2 + cs * 0.5, k === 0 ? 0 : Math.PI)
      out.push(part('plate', q, [cs, cs * 0.6, 14], { rot: [0, k * 0.4, th + (k === 0 ? -0.5 : 0.5)], step: 4, role: 'accent' }))
    }
    // Outrigger arms with pod clusters (step 5).
    for (let k = 0; k < outI; k++) {
      const phi = (k / Math.max(1, outI)) * TAU + p[4] * TAU
      const t = 0.3 + 0.2 * k
      const root = at(t, D / 2, phi)
      const len = 0.28 * H
      const d: [number, number, number] = [Math.cos(phi) * u[0], 0.25, Math.sin(phi)]
      const tip: Vec3 = [root[0] + d[0] * len, root[1] + d[1] * len, root[2] + d[2] * len]
      out.push(...mast(root, d, len, 5).slice(0, 1))
      for (let j = 0; j < 3; j++) out.push(pod(pr, tip, [d[0], 1, d[2]], 1.1, 5))
    }
    // Guyed masts (step 5).
    for (let k = 0; k < mastI + 1; k++) {
      const phi = (k / (mastI + 1)) * TAU + 0.3
      const root = at(1, D * 0.3, phi)
      const d: [number, number, number] = [a[0] + 0.35 * Math.cos(phi), a[1], 0.35 * Math.sin(phi)]
      const m = Math.hypot(d[0], d[1], d[2])
      out.push(...mast(root, [d[0] / m, d[1] / m, d[2] / m], (0.22 + 0.08 * k) * H, 5))
    }
    return out
  },
}

// ── Listening Sails / Holdfast ─────────────────────────────────────────────────────────────────────────────────────
export const listeningSailsHoldfast: FamilyGrammar = {
  kind: 'listeningSails',
  maker: 'holdfast',
  slots: [
    { name: 'panels', options: 4 }, // pair, tri, cross, hex
    { name: 'shape', options: 4 }, // rect, wide-out, narrow-out, two-section
    { name: 'sailSize', options: 4, site: lumClass }, // star size → panel size
    { name: 'rake', options: 4 }, // radial, raised V, drooped, alternating
    { name: 'foot', options: 4 }, // fat drum, sphere, stepped drums, wide frustum
    { name: 'mast', options: 3 }, // 1/2/3 spun drums on the mast
    { name: 'height', options: 3 }, // panels low, mid, high on the mast
    { name: 'crown', options: 4 }, // dome, sphere, spindle, stepped cap
    { name: 'holds', options: 4, site: regionIndex }, // secondary holds round the foot: gate none → rim four
  ],
  build(g, site, stage) {
    const [panI, shapeI, , rakeI, footI, mastI, hI, crownI, holdI] = g.slots
    const p = g.props
    const H = 2300 + 900 * p[0]
    const out: MegaPart[] = []
    const fw = 0.42 * H * (0.9 + 0.2 * p[1])
    let footTop: number
    if (footI === 0) {
      footTop = 0.2 * H
      out.push(part('drum', [0, footTop / 2, 0], [fw, footTop, fw]))
    } else if (footI === 1) {
      footTop = 0.36 * H
      out.push(part('sphere', [0, footTop / 2, 0], [fw, footTop, fw]))
    } else if (footI === 2) {
      footTop = 0.24 * H
      out.push(part('drum', [0, 0.07 * H, 0], [fw, 0.14 * H, fw]))
      out.push(part('drum', [0, 0.19 * H, 0], [fw * 0.62, 0.1 * H, fw * 0.62]))
    } else {
      footTop = 0.18 * H
      out.push(part('frustum', [0, footTop / 2, 0], [fw * 1.25, footTop, fw * 1.25], { taper: 0.45 }))
    }
    for (let k = 0; k < holdI; k++) {
      const b = (k / holdI) * TAU + 0.4
      out.push(radial('drum', b, fw * 0.62, 0.07 * H, [0.12 * H, 0.14 * H, 0.12 * H], { step: 5 }))
    }
    // The slim spun mast (step 2).
    const nm = mastI + 1
    const mastH = 0.62 * H * (0.9 + 0.2 * p[2])
    const md = 0.13 * H
    const seg = mastH / nm
    for (let i = 0; i < nm; i++) {
      const d = md * (i % 2 === 0 ? 1 : 0.72)
      out.push(part('drum', [0, footTop + seg * (i + 0.5), 0], [d, seg * 0.94, d], { step: 2 }))
    }
    const top = footTop + mastH
    // Panels: half at step 2, half at step 3; each unfolds one stage after it is built.
    const n = [2, 3, 4, 6][panI]
    const ls = lumScale(site)
    const L = 0.42 * H * ls * (panI === 3 ? 0.8 : 1)
    const W = 0.3 * H * ls * (panI === 3 ? 0.8 : 1)
    const yAttach = footTop + mastH * [0.25, 0.5, 0.72][hI]
    for (let i = 0; i < n; i++) {
      const b = (i / n) * TAU + p[3] * 0.6
      const step = i % 2 === 0 ? 2 : 3
      const unfolded = stage > step
      const rake = [0, 0.38, -0.3, i % 2 === 0 ? 0.35 : -0.25][rakeI]
      const delta = unfolded ? rake : -1.35
      const taper = [1, 1.6, 0.55, 1][shapeI]
      const len = shapeI === 3 ? L * 0.6 : L
      const r = md * 0.5 + (len / 2) * Math.cos(delta)
      const y = yAttach + (len / 2) * Math.sin(delta)
      out.push(radial('plate', b, r, y, [W, len, 24], { rot: [0, 0, -Math.PI / 2 + delta], taper, step, role: 'signature' }))
      if (shapeI === 3) {
        const r2 = md * 0.5 + (len + len * 0.35) * Math.cos(delta)
        const y2 = yAttach + (len + len * 0.35) * Math.sin(delta) + (unfolded ? W * 0.18 : 0)
        out.push(radial('plate', b, r2, y2, [W * 0.7, len * 0.62, 24], { rot: [0, 0, -Math.PI / 2 + delta], step, role: 'signature' }))
      }
    }
    // Crown (step 5).
    if (crownI === 0) out.push(part('sphere', [0, top, 0], [md * 1.6, md * 0.9, md * 1.6], { step: 5 }))
    else if (crownI === 1) {
      out.push(part('sphere', [0, top + md * 0.9, 0], [md * 2.1, md * 2.1, md * 2.1], { step: 5 }))
    } else if (crownI === 2) {
      out.push(part('frustum', [0, top + 0.12 * H, 0], [md * 0.8, 0.24 * H, md * 0.8], { taper: 0.1, step: 5 }))
    } else {
      out.push(part('drum', [0, top + 0.03 * H, 0], [md * 1.7, 0.06 * H, md * 1.7], { step: 5 }))
      out.push(part('frustum', [0, top + 0.09 * H, 0], [md * 1.1, 0.06 * H, md * 1.1], { taper: 0.5, step: 5 }))
    }
    return out
  },
}

// ── Commons / Mutual ───────────────────────────────────────────────────────────────────────────────────────────────
export const commonsMutual: FamilyGrammar = {
  kind: 'commons',
  maker: 'mutual',
  slots: [
    { name: 'arc', options: 4 }, // 200°, 240°, 280°, 310° of wheel left
    { name: 'gap', options: 4 }, // break at top, upper-left, upper-right, side
    { name: 'tube', options: 3 }, // tube 1/8, 1/6.5, 1/5.5 of the diameter
    { name: 'village', options: 4 }, // one leg, both legs, crown-heavy, base pile
    { name: 'density', options: 4 }, // 20/30/42/56 pods
    { name: 'masts', options: 4 }, // 1..4 guyed masts
    { name: 'perch', options: 3 }, // free, a rock under one leg, a rock cradle
    { name: 'collectors', options: 4, site: lumClass },
    { name: 'dock', options: 4, site: gateQuadrant }, // the rescue dock spar faces the gate
  ],
  build(g, site) {
    const [arcI, gapI, tubeI, villI, densI, mastI, perchI, , dockI] = g.slots
    const p = g.props
    const Dw = 2300 + 900 * p[0]
    const R = Dw / 2
    const tube = Dw / [8, 6.5, 5.5][tubeI]
    const arc = [200, 240, 280, 310][arcI] * (Math.PI / 180)
    const gapAt = [Math.PI / 2, Math.PI / 2 + 0.6, Math.PI / 2 - 0.6, 0][gapI]
    // The arc prim is centred on +y; turn it so its gap sits at `gapAt`.
    const rotZ = gapAt - Math.PI / 2 + Math.PI
    const cy = R + tube / 2 + 0.04 * Dw
    const out: MegaPart[] = []
    out.push(part('relicArc', [0, cy, 0], [Dw, Dw, tube], { arc, rot: [0, 0, rotZ], role: 'relic' }))
    // s in [0,1] walks the surviving arc from one broken end to the other.
    const pointOn = (s: number, r: number): Vec3 => {
      const ang = gapAt + (TAU - arc) / 2 + s * arc
      return [Math.cos(ang) * r, cy + Math.sin(ang) * r, 0]
    }
    if (perchI === 1) out.push(part('rock', [pointOn(0.5, R)[0] * 0.6, 0.06 * Dw, 0], [0.4 * Dw, 0.16 * Dw, 0.3 * Dw], { seed: g.detail, role: 'work' }))
    if (perchI === 2) out.push(part('rock', [0, 0.08 * Dw, 0], [0.75 * Dw, 0.2 * Dw, 0.45 * Dw], { seed: g.detail, role: 'work' }))
    // The village: pods bolted outward and upward, in chandlery colours.
    const nPods = [20, 30, 42, 56][densI]
    const pr = detailRng(g.detail, 21)
    for (let i = 0; i < nPods; i++) {
      let s: number
      const u = rf(pr, 0, 1)
      if (villI === 0) s = u * 0.35
      else if (villI === 1) s = u < 0.5 ? u * 0.5 : 1 - (u - 0.5) * 0.5
      else if (villI === 2) s = 0.3 + u * 0.4
      else s = 0.5 + (u - 0.5) * 0.45
      const q = pointOn(s, R + tube * 0.4)
      const ang = gapAt + (TAU - arc) / 2 + s * arc
      const o: [number, number, number] = [Math.cos(ang) * 0.6, Math.max(0.4, Math.sin(ang)) + 0.4, rf(pr, -0.6, 0.6)]
      const stack = villI === 3 ? 1 + ri(pr, 3) : 1
      for (let k = 0; k < stack; k++) {
        const qq: Vec3 = [q[0], q[1] + k * 140, q[2] + rf(pr, -tube * 0.4, tube * 0.4)]
        out.push(pod(pr, qq, o, 1.1 + 0.4 * p[1], 2 + Math.floor((3 * i) / nPods)))
      }
    }
    // Collector plates sized to the star (step 4).
    const cs = lumScale(site) * 0.22 * Dw
    out.push(part('plate', pointOn(0.15, R + tube + cs * 0.4), [cs, cs * 0.55, 14], { rot: [0, 0.5, 0.6], step: 4, role: 'accent' }))
    out.push(part('plate', pointOn(0.85, R + tube + cs * 0.4), [cs, cs * 0.55, 14], { rot: [0, -0.5, -0.6], step: 4, role: 'accent' }))
    // The rescue dock: a sealed spar across the gap, its tug cradle facing the gate (step 3).
    const dockB = (dockI * Math.PI) / 2
    const e0 = pointOn(0, R)
    const e1 = pointOn(1, R)
    const mid: Vec3 = [(e0[0] + e1[0]) / 2, (e0[1] + e1[1]) / 2, 0]
    const span = Math.hypot(e1[0] - e0[0], e1[1] - e0[1])
    const ang = Math.atan2(e1[1] - e0[1], e1[0] - e0[0])
    out.push(part('drum', mid, [tube * 0.5, span, tube * 0.5], { rot: [0, 0, ang - Math.PI / 2], step: 3 }))
    out.push(part('podTier', [mid[0] + Math.cos(dockB) * tube, mid[1], Math.sin(dockB) * tube * 1.5], [tube * 1.2, tube * 0.9, tube * 1.2], { step: 3 }))
    // Masts with the co-op mark (step 5).
    for (let k = 0; k <= mastI; k++) {
      const s = (k + 0.5) / (mastI + 1)
      const q = pointOn(s, R + tube / 2)
      const ang2 = gapAt + (TAU - arc) / 2 + s * arc
      const d: [number, number, number] = [Math.cos(ang2) * 0.5, 0.85, 0.15]
      const m = Math.hypot(d[0], d[1], d[2])
      out.push(...mast(q, [d[0] / m, d[1] / m, d[2] / m], (0.18 + 0.05 * k) * Dw, 5))
    }
    out.push(part('plate', pointOn(0.5, R + tube * 0.6), [0.3 * Dw, 60, 20], { step: 5, role: 'accent', rot: [0, 0, 0] }))
    return out
  },
}
